import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase';
import axios from 'axios';

const TimeOff = () => {
  const { employeeContext } = useAuth();
  const { companyId, employeeId } = employeeContext || {};
  
  const [ptoData, setPtoData] = useState({ balanceDays: 0, requests: {} });
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    type: 'vacation',
    startDate: '',
    endDate: '',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!companyId || !employeeId) return;

    const ptoRef = ref(database, `companies/${companyId}/employees/${employeeId}/pto`);
    const unsubscribe = onValue(ptoRef, (snap) => {
      setPtoData(snap.val() || { balanceDays: 0, requests: {} });
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId, employeeId]);

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";
      console.log('Submitting PTO request to:', backendUrl);
      
      await axios.post(`${backendUrl}/api/pto/request`, {
        companyId,
        employeeId,
        ...requestForm
      });
      setShowRequestForm(false);
      setRequestForm({ type: 'vacation', startDate: '', endDate: '', reason: '' });
      alert('Request submitted successfully!');
    } catch (error) {
      console.error('Error submitting PTO request:', error);
      alert('Failed to submit request: ' + (error.response?.data?.detail || error.message));
    }
    setSubmitting(false);
  };

  if (loading) return <div>Loading PTO data...</div>;

  const requestsList = Object.values(ptoData.requests || {}).sort((a, b) => b.requestedAt - a.requestedAt);

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Time Off</h1>
        <button className="btn-primary" onClick={() => setShowRequestForm(!showRequestForm)}>
          {showRequestForm ? 'Cancel Request' : 'New Request'}
        </button>
      </div>

      {/* Balance Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div className="card" style={{ textAlign: 'center', padding: '30px' }}>
          <div style={{ fontSize: '36px', fontWeight: 'bold', color: 'var(--brand-primary)', marginBottom: '10px' }}>
            {ptoData.balanceDays}
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>Days Annual Leave Remaining</div>
        </div>
        {/* Can add more balances like Sick Leave if schema supports it explicitly */}
      </div>

      {/* Request Form */}
      {showRequestForm && (
        <div className="card" style={{ marginBottom: '30px', border: '1px solid var(--brand-primary)' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '20px' }}>New Time Off Request</h2>
          <form onSubmit={handleRequestSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Type</label>
              <select 
                value={requestForm.type}
                onChange={(e) => setRequestForm({...requestForm, type: e.target.value})}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--card-border)', background: '#031128', color: 'white' }}
              >
                <option value="vacation">Vacation</option>
                <option value="sick">Sick Leave</option>
                <option value="personal">Personal</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '20px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Start Date</label>
                <input 
                  type="date" 
                  value={requestForm.startDate}
                  onChange={(e) => setRequestForm({...requestForm, startDate: e.target.value})}
                  required
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--card-border)', background: '#031128', color: 'white' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>End Date</label>
                <input 
                  type="date" 
                  value={requestForm.endDate}
                  onChange={(e) => setRequestForm({...requestForm, endDate: e.target.value})}
                  required
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--card-border)', background: '#031128', color: 'white' }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Reason (Optional)</label>
              <textarea 
                value={requestForm.reason}
                onChange={(e) => setRequestForm({...requestForm, reason: e.target.value})}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--card-border)', background: '#031128', color: 'white' }}
                rows="3"
              />
            </div>

            <button type="submit" disabled={submitting} className="btn-primary" style={{ alignSelf: 'flex-start' }}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </div>
      )}

      {/* History */}
      <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>Request History</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {requestsList.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No past requests.</p>
        ) : (
          requestsList.map((req) => (
            <div key={req.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '5px', textTransform: 'capitalize' }}>{req.type}</div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                  {new Date(req.startDate).toLocaleDateString()} - {new Date(req.endDate).toLocaleDateString()}
                </div>
                {req.comment && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '5px' }}>"{req.comment}"</div>}
              </div>
              <div style={{ 
                padding: '5px 10px', 
                borderRadius: '20px', 
                fontSize: '12px', 
                fontWeight: 'bold',
                backgroundColor: 
                  req.status === 'approved' ? 'rgba(64, 235, 110, 0.2)' : 
                  req.status === 'rejected' ? 'rgba(235, 64, 64, 0.2)' : 
                  'rgba(235, 221, 64, 0.2)',
                color: 
                  req.status === 'approved' ? '#40EB6E' : 
                  req.status === 'rejected' ? '#EB4040' : 
                  '#EBDD40'
              }}>
                {req.status.toUpperCase()}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TimeOff;



